import React, { useEffect, useState } from 'react'
import { Alert } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import { loginWithOTP, getUserDetails } from '../utils/AuthUtil'
import LoadingButton from '@mui/lab/LoadingButton'
import ReactGA4 from 'react-ga4'
import PostHogTracker from '../utils/PostHogTracker'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../Components/ui/input-otp'
import { REGEXP_ONLY_DIGITS } from 'input-otp'
import CreateSharpIcon from '@mui/icons-material/CreateSharp'

const LoginOtp = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [otpEntered, setOtpEntered] = useState(undefined)
  const [errorMessage, setErrorMessage] = useState(null)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (location) {
      setPhoneNumber(location.state?.phoneNumber)
    }
  }, [])

  const otpLogin = async (event) => {
    event.preventDefault()
    setErrorMessage(null)
    const payload = {
      phoneNumber,
      otp: otpEntered,
    }
    try {
      ReactGA4.event({
        category: 'Login',
        action: 'Login OTP Entered',
      })
      PostHogTracker.otpEntered('login')

      const response = await loginWithOTP(payload)

      setLoading(false)
      if (response.isError) {
        if (response.code === 'Unauthorized') {
          setErrorMessage('Enter Valid OTP.')
        } else {
          setErrorMessage('Unable to process your request.Please try again')
        }
        ReactGA4.event({
          category: 'Login',
          action: 'OTP Failure',
        })
        PostHogTracker.otpFailure('login', 'invalid_otp')
        return
      }
      const userDetails = getUserDetails()
      // console.log('GA event', userDetails.email)
      // Send logged in user details to GA4 and PostHog
      ReactGA4.event({
        category: 'Login',
        action: 'Login Complete',
        value: userDetails.email,
      })
      ReactGA4.set({ userId: userDetails.email })

      // Use enhanced PostHog identification with super properties
      // Using phone number as distinct ID (works for both KYC and non-KYC users)
      PostHogTracker.loginCompleted(phoneNumber, userDetails, 'otp')

      if (window.utmData && window.utmData['$marketing_title']) {
        if (window.utmData['$marketing_title'] === 'Insurance') {
          navigate('/dashboard/insurance')
        } else {
          let navState = null
          if (window.utmData.navigation_state) {
            navState = JSON.parse(window.utmData.navigation_state)
            // console.log(navState)
          }
          if (window.utmData.site_url) {
            navigate(window.utmData.site_url, { state: navState })
          } else {
            navigate('/dashboard', {
              state: { login: true, firstTimeUser: false, hasSeenVideo: true },
            })
          }
        }
      } else {
        navigate('/dashboard', { state: { login: true, firstTimeUser: false, hasSeenVideo: true } })
      }
    } catch (e) {
      setLoading(false)
    }
  }

  return (
    <div className='mx-auto flex w-full max-w-[360px] min-w-0 flex-col items-center justify-center lg:relative lg:min-w-[432px] lg:rounded-3xl lg:border lg:bg-white lg:px-[72px] lg:py-12'>
      <div className='flex flex-col gap-y-1 mb-6'>
        <h1 className='text-center font-IBM text-[32px] font-bold leading-10 text-black-950'>
          Login
        </h1>
        <p className='flex items-center justify-center gap-x-1 text-center font-IBM text-sm font-medium leading-5 text-black-400'>
          Welcome back!
        </p>
      </div>

      <div className='w-full mb-6 flex flex-col justify-center items-center'>
        <p className='flex flex-row whitespace-nowrap font-IBM text-sm font-medium leading-5 text-black-400'>
          Enter the OTP sent to
        </p>
        <div
          className='flex justify-center items-center gap-x-1 border-primary-500 border-dashed w-min border-b-[2px]'
          onClick={() => navigate('/login')}
        >
          <p className='cursor-pointer whitespace-nowrap font-IBM font-semibold text-primary-500'>
            {phoneNumber}
          </p>
          <CreateSharpIcon
            className='cursor-pointer text-primary-500 text-[16px]'
            sx={{ fontSize: '16px' }}
          />
        </div>
      </div>

      <Grid item xs={12} sx={{ mb: 2 }}>
        {' '}
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
      </Grid>
      <Grid item xs={12} sx={{ mb: 2 }}>
        <form onSubmit={otpLogin}>
          <div>
            <InputOTP
              maxLength={6}
              pattern={REGEXP_ONLY_DIGITS}
              value={otpEntered}
              onChange={(value) => setOtpEntered(value)}
              containerClassName='w-full'
            >
              <InputOTPGroup className='w-full justify-between'>
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={0}
                />
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={1}
                />
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={2}
                />
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={3}
                />
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={4}
                />
                <InputOTPSlot
                  className={`${
                    errorMessage && 'border-error-600 text-error-600 font-medium'
                  } h-11 w-9 rounded-xl border-[#D3DBEC] px-0 text-base leading-6 min-[360px]:h-12 min-[360px]:w-11`}
                  index={5}
                />
              </InputOTPGroup>
            </InputOTP>

            <div className='text-center text-xs text-red-600 font-medium mt-2'>
              {errorMessage && <>{errorMessage}</>}
            </div>
            {/* <TextField
              required
              id='component-outlined'
              type='text'
              size='small'
              onChange={handleChange}
              fullWidth
              autoFocus
              value={otpEntered}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
              }}
            /> */}
          </div>
          <LoadingButton
            variant='contained'
            loading={loading}
            // loadingPosition='end'
            sx={{
              mt: '24px',
              py: '12px',
              px: '24px',
              fontSize: '14px',
              lineHeight: '20px',
              fontWeight: '600',
              fontFamily: 'IBM Plex Sans',
              textTransform: 'none',
              borderRadius: '999px',
              boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
              '&:hover': {
                backgroundColor: '#2818ff',
                boxShadow: '0 10px 24px rgba(40, 24, 255, 0.18)',
              },
              '&:active': { backgroundColor: '#2818ff', boxShadow: 'none' },
              '&.Mui-disabled': {
                backgroundColor: '#8c91b8',
                color: '#fff',
                opacity: 1,
              },
            }}
            disabled={!(otpEntered?.length === 6)}
            fullWidth
            onClick={(event) => {
              setLoading(true)
              otpLogin(event)
            }}
          >
            Verify OTP
          </LoadingButton>
        </form>
      </Grid>
    </div>
  )
}

export default LoginOtp
